import axios from "axios";

import { GeometryType } from "components/map/utils/geometryUtils";

export enum TilesStatus {
  Pending = "pending",
  Processing = "processing",
  ProcessingHighZoom = "processing_high_zoom",
  LowZoomReady = "low_zoom_ready",
  Completed = "completed",
  Failed = "failed"
}

export interface ShapefileTilesStatusRequest {
  shapefileIds: string[];
}

export interface ShapefileTilesStatusItem {
  shapefileId: string;
  tilesStatus: TilesStatus;
  tilesReady: boolean | null;
}

export type ShapefileTilesStatusResponse = ShapefileTilesStatusItem[];

export async function getShapefilesTilesStatus(
  shapefileIds: string[]
): Promise<ShapefileTilesStatusResponse> {
  const response = await axios.post<ShapefileTilesStatusResponse>(
    `${process.env.REACT_APP_PROJECT_SERVICE}/shapefiles/tiles-status`,
    { shapefileIds }
  );
  return response.data;
}
const shapetileServiceEndpoint = process.env.REACT_APP_SHAPETILE_SERVICE;

export interface TileJSONVectorLayer {
  id: string;
  fields?: Record<string, string>;
  minzoom?: number;
  maxzoom?: number;
  geometry_type?: GeometryType;
}

export interface TileJSONResponse {
  tilejson: string;
  name?: string;
  version?: string;
  scheme?: string;
  tiles: string[];
  minzoom: number;
  maxzoom: number;
  bounds: number[];
  center?: number[];
  geometry_type?: GeometryType;
}

/**
 * Constructs the TileJSON URL for a shapefile
 * @param shapefileId The shapefile ID
 * @returns The TileJSON URL
 */
export function getShapefileTileJSONUrl(shapefileId: string): string {
  const encodedShapefileId = encodeURIComponent(shapefileId);
  return `${shapetileServiceEndpoint}/tiles/${encodedShapefileId}/tilejson.json`;
}

/**
 * Constructs the TileJSON URL for a system shapefile
 * @param shapefileId The shapefile ID
 * @returns The TileJSON URL
 */
export function getSystemShapefileTileJSONUrl(shapefileId: string): string {
  const encodedShapefileId = encodeURIComponent(shapefileId);
  return `${shapetileServiceEndpoint}/system/tiles/${encodedShapefileId}/tilejson.json`;
}

/**
 * Fetches TileJSON metadata for a shapefile
 * @param shapefileId The shapefile ID
 * @param source Optional source type - use 'system' for system shapefiles
 * @returns Promise resolving to TileJSON response
 */
export async function fetchShapefileTileJSON(
  shapefileId: string,
  source?: string
): Promise<TileJSONResponse> {
  const url =
    source === "McDaniel"
      ? getSystemShapefileTileJSONUrl(shapefileId)
      : getShapefileTileJSONUrl(shapefileId);
  const response = await axios.get<TileJSONResponse>(url);

  return response.data;
}

export interface BatchTileJSONRequest {
  shapefileIds: string[]; // Org-specific shapefiles
  systemShapefileIds: string[]; // System-wide shapefiles
}

export interface BatchTileJSONResponse {
  [shapefileId: string]: TileJSONResponse;
}

/**
 * Fetches TileJSON metadata for multiple shapefiles
 * @param shapefileIds Array of regular shapefile IDs
 * @param systemShapefileIds Array of system shapefile IDs
 * @returns Promise resolving to map of shapefileId to TileJSON
 */
export async function fetchBatchTileJSON(
  shapefileIds: string[],
  systemShapefileIds: string[]
): Promise<BatchTileJSONResponse> {
  const response = await axios.post<BatchTileJSONResponse>(
    `${shapetileServiceEndpoint}/tiles/tilejson`,
    {
      shapefileIds,
      systemShapefileIds
    }
  );

  return response.data;
}
